// src/components/AuctionOrderCountdown.tsx
// Countdown timer para órdenes de subasta con deadline de pago

import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AuctionOrderCountdownProps {
  deadline: string; // ISO timestamp
  onExpire?: () => void;
  className?: string;
  showIcon?: boolean;
}

export function AuctionOrderCountdown({ 
  deadline, 
  onExpire, 
  className,
  showIcon = true 
}: AuctionOrderCountdownProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const deadlineTime = new Date(deadline).getTime();
      const difference = deadlineTime - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft(0);
        if (onExpire) {
          onExpire();
        }
        return;
      }

      setTimeLeft(Math.floor(difference / 1000)); // segundos
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [deadline, onExpire]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Colores según urgencia
  const getColorClasses = () => {
    if (isExpired) {
      return 'text-red-600 dark:text-red-400';
    }
    
    const hours = timeLeft / 3600;
    if (hours > 8) {
      return 'text-green-600 dark:text-green-400'; // Más de 8h: verde
    } else if (hours > 2) {
      return 'text-amber-600 dark:text-amber-400'; // Entre 2-8h: amarillo
    } else {
      return 'text-red-600 dark:text-red-400'; // Menos de 2h: rojo
    }
  };

  if (isExpired) {
    return (
      <div className={cn('flex items-center gap-1.5', getColorClasses(), className)}>
        {showIcon && <AlertCircle className="h-4 w-4 flex-shrink-0" />}
        <span className="text-sm font-semibold">Tiempo agotado</span>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-1.5', getColorClasses(), className)}>
      {showIcon && <Clock className="h-4 w-4 flex-shrink-0 animate-pulse" />}
      <span className="text-sm font-semibold">
        {formatTime(timeLeft)}
      </span>
    </div>
  );
}
